import type { ModalProps } from '@workramp/ui';
import type { ReactNode } from 'react';
import { Col, Modal, ModalBody, ModalFooter, ModalHeader, Row, Stack, Button, Body } from '@workramp/ui';
import { useTheme } from 'styled-components';
import { I18n } from '~/common/explicit-exports';

export interface ConfirmModalProps extends Omit<ModalProps, 'children' | 'onClose' | 'isOpen'> {
  onCancel: () => void;
  onConfirm: () => void;
  title: string;
  body?: ReactNode;
  variant?: 'danger' | 'primary';
  cancel?: string;
  confirm?: string;
  isOpen: boolean;
}

export const ConfirmModal = ({
  onCancel,
  onConfirm,
  title,
  body,
  cancel,
  confirm,
  variant = 'danger',
  ...modalProps
}: ConfirmModalProps) => {
  const theme = useTheme();
  const CANCEL = cancel || I18n.t('js.common.modal.cancel');
  const CONFIRM = confirm || I18n.t('js.common.modal.confirm');
  return (
    <Modal onClose={onCancel} {...modalProps}>
      <ModalHeader title={title} />
      {body && (
        <ModalBody>
          <Stack size={4}>
            {typeof body === 'string' ? (
              <Body level="md" color={theme.colors.on[600]}>
                {body}
              </Body>
            ) : (
              body
            )}
          </Stack>
        </ModalBody>
      )}
      <ModalFooter>
        <Row justify="flex-end" gapX={2}>
          <Col xs="auto">
            <Button data-testid="button-cancel" color="secondary" variant="text" onPress={onCancel} autoFocus>
              {CANCEL}
            </Button>
          </Col>
          <Col xs="auto">
            <Button
              data-testid="button-confirm"
              color={variant}
              variant={variant === 'danger' ? 'outline' : 'solid'}
              onPress={onConfirm}
            >
              {CONFIRM}
            </Button>
          </Col>
        </Row>
      </ModalFooter>
    </Modal>
  );
};
